import React from 'react';
import { Paper, Typography, Box } from '@mui/material';
import {
    BarChart,
    Bar,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';

export interface CountryYieldRow {
    country: string;
    avgYieldGross: number;
}

const getFieldValue = (row: Record<string, any>, fieldNames: string[]) => {
    for (const fieldName of fieldNames) {
        const exact = row[fieldName];
        if (exact !== undefined) return exact;
        const matchedKey = Object.keys(row).find((key) => key.toLowerCase() === fieldName.toLowerCase());
        if (matchedKey) return row[matchedKey];
    }
    return undefined;
};

const toNumericYield = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const sanitized = String(value).replace('%', '').trim();
    const parsed = Number(sanitized);
    return Number.isFinite(parsed) ? parsed : null;
};

export const buildAverageYieldByCountry = (rows: any[], assetClass: string): CountryYieldRow[] => {
    const grouped = new Map<string, { total: number; count: number }>();
    rows.forEach((row) => {
        const classValue = String(getFieldValue(row, ['ASSET_CLASS', 'asset_class']) ?? '')
            .trim()
            .toLowerCase();
        if (classValue !== assetClass.toLowerCase()) return;
        const countryValue = String(getFieldValue(row, ['Country', 'country']) ?? '').trim() || 'Unknown';
        const yieldGross = toNumericYield(getFieldValue(row, ['yield_gross', 'YIELD_GROSS']));
        if (yieldGross === null) return;
        const current = grouped.get(countryValue) ?? { total: 0, count: 0 };
        current.total += yieldGross;
        current.count += 1;
        grouped.set(countryValue, current);
    });
    return Array.from(grouped.entries())
        .map(([country, values]) => ({
            country,
            avgYieldGross: Number((values.total / values.count).toFixed(2)),
        }))
        .sort((a, b) => b.avgYieldGross - a.avgYieldGross);
};

export const buildMaxYieldByCountry = (rows: any[], assetClass: string): CountryYieldRow[] => {
    const grouped = new Map<string, number[]>();
    rows.forEach((row) => {
        const classValue = String(getFieldValue(row, ['ASSET_CLASS', 'asset_class']) ?? '')
            .trim()
            .toLowerCase();
        if (classValue !== assetClass.toLowerCase()) return;
        const countryValue = String(getFieldValue(row, ['Country', 'country']) ?? '').trim() || 'Unknown';
        const yieldGross = toNumericYield(getFieldValue(row, ['yield_gross', 'YIELD_GROSS']));
        if (yieldGross === null) return;
        const arr = grouped.get(countryValue) ?? [];
        arr.push(yieldGross);
        grouped.set(countryValue, arr);
    });
    return Array.from(grouped.entries())
        .map(([country, values]) => ({
            country,
            avgYieldGross: Number(Math.max(...values).toFixed(2)),
        }))
        .sort((a, b) => b.avgYieldGross - a.avgYieldGross);
};

export const buildMinYieldByCountry = (rows: any[], assetClass: string): CountryYieldRow[] => {
    const grouped = new Map<string, number[]>();
    rows.forEach((row) => {
        const classValue = String(getFieldValue(row, ['ASSET_CLASS', 'asset_class']) ?? '')
            .trim()
            .toLowerCase();
        if (classValue !== assetClass.toLowerCase()) return;
        const countryValue = String(getFieldValue(row, ['Country', 'country']) ?? '').trim() || 'Unknown';
        const yieldGross = toNumericYield(getFieldValue(row, ['yield_gross', 'YIELD_GROSS']));
        if (yieldGross === null) return;
        const arr = grouped.get(countryValue) ?? [];
        arr.push(yieldGross);
        grouped.set(countryValue, arr);
    });
    return Array.from(grouped.entries())
        .map(([country, values]) => ({
            country,
            avgYieldGross: Number(Math.min(...values).toFixed(2)),
        }))
        .sort((a, b) => b.avgYieldGross - a.avgYieldGross);
};

export const CountryYieldCharts: React.FC<{ data: any[] }> = ({ data }) => {
    const residentialChartData = buildAverageYieldByCountry(data, 'residential');
    const officeChartData = buildAverageYieldByCountry(data, 'office');
    const industrialChartData = buildAverageYieldByCountry(data, 'industrial');
    const hospitalityChartData = buildAverageYieldByCountry(data, 'hospitality');
    const retailChartData = buildAverageYieldByCountry(data, 'retail');

    const officeMaxData = buildMaxYieldByCountry(data, 'office');
    const officeMinData = buildMinYieldByCountry(data, 'office');
    const industrialMaxData = buildMaxYieldByCountry(data, 'industrial');
    const industrialMinData = buildMinYieldByCountry(data, 'industrial');
    const hospitalityMaxData = buildMaxYieldByCountry(data, 'hospitality');
    const hospitalityMinData = buildMinYieldByCountry(data, 'hospitality');
    const retailMaxData = buildMaxYieldByCountry(data, 'retail');
    const retailMinData = buildMinYieldByCountry(data, 'retail');

    const residentialMaxData = buildMaxYieldByCountry(data, 'residential');
    const residentialMinData = buildMinYieldByCountry(data, 'residential');

    const renderCountryYieldChart = (
        title: string,
        rows: CountryYieldRow[],
        dataKey: string = 'avgYieldGross',
        barName: string = 'Avg Yield Gross',
        fill: string = 'var(--border-color, #1976d2)'
    ) => (
        <Paper
            sx={{
                p: 2,
                backgroundColor: 'var(--background-color)',
                color: 'var(--text-color)',
                border: '1px solid var(--border-color, #e2e8f0)',
            }}
        >
            <Typography sx={{ fontWeight: 700, mb: 1.5 }}>{title}</Typography>
            {rows.length === 0 ? (
                <Typography variant='body2' sx={{ color: 'var(--text-color)' }}>
                    No matching data
                </Typography>
            ) : (
                <Box sx={{ width: '100%', height: Math.max(280, rows.length * 36) }}>
                    <ResponsiveContainer>
                        <BarChart data={rows} layout='vertical' margin={{ top: 10, right: 18, left: 10, bottom: 10 }}>
                            <CartesianGrid stroke='var(--border-color, #e2e8f0)' strokeDasharray='3 3' />
                            <XAxis
                                type='number'
                                tickFormatter={(value) => `${value}%`}
                                tick={{ fill: 'var(--text-color)', fontSize: 12 }}
                                axisLine={{ stroke: 'var(--border-color, #e2e8f0)' }}
                                tickLine={{ stroke: 'var(--border-color, #e2e8f0)' }}
                            />
                            <YAxis
                                type='category'
                                dataKey='country'
                                width={120}
                                tick={{ fill: 'var(--text-color)', fontSize: 12 }}
                                axisLine={{ stroke: 'var(--border-color, #e2e8f0)' }}
                                tickLine={{ stroke: 'var(--border-color, #e2e8f0)' }}
                            />
                            <Tooltip
                                formatter={(value: number) => `${value}%`}
                                contentStyle={{
                                    backgroundColor: 'var(--background-color)',
                                    color: 'var(--text-color)',
                                    border: '1px solid var(--border-color, #e2e8f0)',
                                }}
                                labelStyle={{ color: 'var(--text-color)' }}
                                itemStyle={{ color: 'var(--text-color)' }}
                            />
                            <Bar dataKey={dataKey} name={barName} fill={fill} radius={[0, 8, 8, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            )}
        </Paper>
    );

    // residential split removed — rendering uses explicit Avg/Max/Min panels

    return (
        <>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 2,
                }}
            >
                {renderCountryYieldChart('Office: Avg Yield Gross by Country', officeChartData, 'avgYieldGross', 'Avg Yield Gross', '#1976d2')}
                {renderCountryYieldChart('Office: Max Yield Gross by Country', officeMaxData, 'avgYieldGross', 'Max Yield Gross', '#2e7d32')}
                {renderCountryYieldChart('Office: Min Yield Gross by Country', officeMinData, 'avgYieldGross', 'Min Yield Gross', '#c62828')}

                {renderCountryYieldChart('Industrial: Avg Yield Gross by Country', industrialChartData, 'avgYieldGross', 'Avg Yield Gross', '#1976d2')}
                {renderCountryYieldChart('Industrial: Max Yield Gross by Country', industrialMaxData, 'avgYieldGross', 'Max Yield Gross', '#2e7d32')}
                {renderCountryYieldChart('Industrial: Min Yield Gross by Country', industrialMinData, 'avgYieldGross', 'Min Yield Gross', '#c62828')}

                {renderCountryYieldChart('Hospitality: Avg Yield Gross by Country', hospitalityChartData, 'avgYieldGross', 'Avg Yield Gross', '#1976d2')}
                {renderCountryYieldChart('Hospitality: Max Yield Gross by Country', hospitalityMaxData, 'avgYieldGross', 'Max Yield Gross', '#2e7d32')}
                {renderCountryYieldChart('Hospitality: Min Yield Gross by Country', hospitalityMinData, 'avgYieldGross', 'Min Yield Gross', '#c62828')}

                {renderCountryYieldChart('Retail: Avg Yield Gross by Country', retailChartData, 'avgYieldGross', 'Avg Yield Gross', '#1976d2')}
                {renderCountryYieldChart('Retail: Max Yield Gross by Country', retailMaxData, 'avgYieldGross', 'Max Yield Gross', '#2e7d32')}
                {renderCountryYieldChart('Retail: Min Yield Gross by Country', retailMinData, 'avgYieldGross', 'Min Yield Gross', '#c62828')}
            </Box>
            <Box sx={{ mt: 4 }}>
                <Typography sx={{ fontWeight: 700, mb: 2, fontSize: 18 }}>
                    Residential: Avg/Max/Min Yield Gross by Country
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                    <Box>
                        {renderCountryYieldChart('Residential: Avg', residentialChartData, 'avgYieldGross', 'Avg Yield Gross', '#1976d2')}
                    </Box>
                    <Box>
                        {renderCountryYieldChart('Residential: Max', residentialMaxData, 'avgYieldGross', 'Max Yield Gross', '#2e7d32')}
                    </Box>
                    <Box>
                        {renderCountryYieldChart('Residential: Min', residentialMinData, 'avgYieldGross', 'Min Yield Gross', '#c62828')}
                    </Box>
                </Box>
            </Box>
        </>
    );
};
